/** Phase 235 — Reusable list/card skeletons for perceived-performance.
 * Drop-in replacements while data is loading; mimic the actual content
 * shape so the layout doesn't jump when data arrives. */

import { Skeleton } from "./ui/skeleton";

/** Vertical list of card-like rows. Used by Jobs / Quotes / Invoices / WO. */
export function CardListSkeleton({ rows = 5, showHeader = true }) {
  return (
    <div className="space-y-3" data-testid="skeleton-card-list">
      {showHeader && (
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-8 w-24" />
        </div>
      )}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rounded-md border border-border p-3 bg-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2 min-w-0">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <div className="flex gap-1.5 mt-2">
                <Skeleton className="h-4 w-14 rounded-full" />
                <Skeleton className="h-4 w-12 rounded-full" />
              </div>
            </div>
            <Skeleton className="h-8 w-8 rounded shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Grid of metric tiles for dashboards. */
export function StatsGridSkeleton({ cols = 4 }) {
  const colClass = cols === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3";
  return (
    <div className={`grid gap-3 ${colClass}`} data-testid="skeleton-stats-grid">
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="rounded-md border border-border p-4 bg-card space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

/** Detail page skeleton — title, meta line, sections. */
export function DetailSkeleton() {
  return (
    <div className="space-y-4" data-testid="skeleton-detail">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/3" />
      <div className="rounded-md border border-border p-4 space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
      <StatsGridSkeleton cols={3} />
    </div>
  );
}

/** Table skeleton — for any tabular list view. */
export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="rounded-md border border-border overflow-hidden" data-testid="skeleton-table">
      <div className="bg-muted/40 grid gap-2 p-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} className="h-3 w-16" />)}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid gap-2 p-3 border-t border-border" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: cols }).map((_, c) => <Skeleton key={c} className="h-4 w-full max-w-[80%]" />)}
        </div>
      ))}
    </div>
  );
}
